<template>
  <div>
    <div class="text-h5">
      <!-- Base information for the apartment -->
      Lgh {{ apartmentDesignation }}
    </div>

    <!-- Selectors for the view -->
    <v-row align="center">
      <!-- display: block -->
      <v-col cols="2">
        <div class="text-body-1 font-weight-bold px-2">Sortera efter</div>
      </v-col>
      <v-col cols="10">
        <!-- display: block -->
        <v-radio-group v-model="sortLinesBy" row>
          <v-radio label="Installationer" value="assetCategory"></v-radio>
          <v-radio label="Utrymme" value="roomName"></v-radio>
        </v-radio-group>
      </v-col>
    </v-row>

    <!-- Iterate over lines -->
    <grouped-list :groupBy="sortLinesBy" :items="assetLines" class="mt-4">
      <!-- display: block -->
      <template v-slot:group-header="{ groupName: category }">
        <div class="text-h5 px-2">{{ category }}</div>
      </template>
      <!-- display: block -->
      <template v-slot:list-item="{ item: line }">
        <div>
          <v-sheet elevation="1" class="my-4 px-2">
            <v-row align="end">
              <v-col cols="2">
                <!-- display: block -->
                <apartment-line-asset
                  :asset="line.asset"
                ></apartment-line-asset>
              </v-col>

              <v-col cols="2">
                <apartment-line-room :room="line.room"></apartment-line-room>
              </v-col>

              <v-col cols="4">
                <!-- display: block -->
                <apartment-line-point-event
                  :pointEvents="
                    line.pointEvents.filter((p) => p.isCommand || p.isSensor)
                  "
                ></apartment-line-point-event>
              </v-col>

              <v-col cols="4">
                <!-- display: block -->
                <apartment-line-point-event
                  :pointEvents="
                    line.pointEvents.filter((p) => p.isStatus || p.isAlarm)
                  "
                ></apartment-line-point-event>
              </v-col>
            </v-row>
          </v-sheet>
        </div>
      </template>
    </grouped-list>
  </div>
</template>

<script>
import Vue from 'vue'
import { RecIndividual } from '@/rec-cache/rec-cache-individual'
import { Iris, Iri } from '@/rec-cache/rec-cache-types'

import { Alarms } from '@/vue-wob-cache/vue-wob-alarms'

import GroupedList from '@/components/GroupedList.vue'
import ApartmentLineRoom from '@/components/ApartmentLineRoom.vue'
import ApartmentLineAsset from '@/components/ApartmentLineAsset.vue'
import ApartmentLinePointEvent from '@/components/ApartmentLinePointEvent.vue'

// TODO find a better name/abstraction for the point-event pair, as point is having a specific meaning in REC/Brick.
class PointEvent {
  constructor({ point: point = null, event: event = null } = {}) {
    this.point = point
    this.event = event
  }
  get isAlarm() {
    return !!this.point && Alarms.includes(this.point.type.value)
  }
  // TODO create schema service to resolve class hierarchies...
  get isCommand() {
    return (
      !!this.point &&
      [
        'https://webofbuildings.com/ontology/PhysicalDigitalKeyCredentialsCommand',
        'https://webofbuildings.com/ontology/OneTimePasswordCredentialsCommand',
      ].includes(this.point.type.value)
    )
  }
  get isStatus() {
    return (
      !!this.point &&
      ['https://brickschema.org/schema/Brick#Open_Close_Status'].includes(
        this.point.type.value
      )
    )
  }
  get isSensor() {
    // TODO add list of relevant sensors...
    return false
  }
  // get isValue() {
  //   return !this.isAlarm
  // }
  get pointType() {
    return this.point.type.short
  }
  get pointName() {
    return this.point?.getValuesByName(Iris.recName)?.[0].value
  }
  get eventName() {
    return this.event?.getValuesByName(Iris.recName)?.[0].value
  }
  get eventValue() {
    return this.event?.getValuesByName(Iris.recValue)?.[0].value
  }
  get eventTimestamp() {
    return this.event?.getValuesByName(Iris.recTimestamp)?.[0].value
  }
  get eventStart() {
    return this.event?.getValuesByName(Iris.recStart)?.[0].value
  }
  get eventEnd() {
    return this.event?.getValuesByName(Iris.recEnd)?.[0].value
  }
  get eventTime() {
    return this.eventTimestamp || this.eventEnd || this.eventStart
  }
  get eventDate() {
    const time = this.eventTime
    return time ? new Date(time) : null
  }
}

const AssetCategories = {
  Access: [
    'https://w3id.org/rec#Door',
    'https://webofbuildings.com/ontology/DigitalLock',
  ],
  Internet: ['https://webofbuildings.com/ontology/WirelessRouter'],
  ElectricalGrid: ['https://brickschema.org/schema/Brick#Breaker_Panel'],
  Networking: [
    'https://webofbuildings.com/ontology/DataSocket',
    'https://webofbuildings.com/ontology/PatchPanel',
    'https://webofbuildings.com/ontology/PatchPort',
  ],
}

class AssetLine {
  constructor({ room = null, asset = null, pointEvents = [] } = {}) {
    const [category] = Object.entries(AssetCategories).find(([, values]) =>
      values.includes(asset.type.value)
    )
    this.category = category || 'Other'
    this.room = room
    this.asset = asset
    this.pointEvents = pointEvents
  }
  get roomName() {
    // TODO should really be based on the 'name' property and potentially take locale into account.
    return this.room.label.value
  }
  get assetCategory() {
    return this.category
  }
}

export default {
  components: {
    GroupedList,
    ApartmentLineRoom,
    ApartmentLineAsset,
    ApartmentLinePointEvent,
  },
  props: {
    id: {
      type: String,
      default: () => '',
    },
  },
  data: () => ({
    individual: new RecIndividual(),
    assetLines: [],
    sortLinesBy: 'assetCategory',
    testGrpList: [
      { room: 'A', asset: 'X' },
      { room: 'B', asset: 'X' },
      { room: 'C', asset: 'Y' },
    ],
  }),
  computed: {
    apartmentDesignation: function () {
      return (
        this.individual.getValuesByName(
          'https://webofbuildings.com/ontology/designation'
        )?.[0].value || '-'
      )
    },
    apartmentId: function () {
      return this.individual.id.short
    },
    // Helper to loop over categories.
    assetCategories: function () {
      return Object.keys(AssetCategories)
    },
  },

  created: function () {
    console.log('views.apartment-view.created')
    this.readApartment(this.id)

    // const roomIds = this.$wob.queryEdges({
    //   source: this.individual.id.value,
    //   label: 'https://w3id.org/rec#includes',
    // })
    // const rooms = roomIds.map((edge) => this.$wob.read(edge.target))
    // console.log(rooms)
  },

  methods: {
    // Parameter id is an id string value.
    readApartment: function (id) {
      console.log('views.apartment-view.methods.read-apartment', id)
      this.individual = Vue.observable(this.$wob.read(new Iri(id), true))
      console.log(
        'views.apartment-view.methods.read-apartment.individual',
        this.individual
      )

      // Find all assets and their paths, apartment-room-asset.
      // Paths to rooms without assets are filtered away.
      const assetPaths = this.getAssets(id).filter((p) => p.length > 2)
      console.log(
        'views.apartment-view.methods.read-apartment.asset-paths',
        assetPaths
      )

      // Find all event paths for each asset, asset-point-event.
      // Will generate an array of arrays, where each array in turn is an array of paths.
      // The returned array is hence reduced / flattened to an array of path arrays.
      const eventPaths = assetPaths
        .map((p) => this.getEvents(p.slice(-1)[0]))
        .reduce((prev, curr) => prev.concat(curr), [])
      console.log(
        'views.apartment-view.methods.read-apartment.event-paths',
        eventPaths
      )

      // Create lines for the individual assets.
      this.assetLines = assetPaths.map(
        (ap) =>
          new AssetLine({
            room: Vue.observable(this.$wob.read(new Iri(ap[1]))),
            asset: Vue.observable(this.$wob.read(new Iri(ap.slice(-1)[0]))),
            pointEvents: eventPaths
              .filter((ep) => ep[0] == ap.slice(-1)[0])
              .map(
                (ep) =>
                  new PointEvent({
                    point: Vue.observable(this.$wob.read(new Iri(ep[1]))),
                    event: Vue.observable(this.$wob.read(new Iri(ep[2]))),
                  })
              ),
          })
      )
      console.log(
        'views.apartment-view.methods.read-apartment.asset-lines',
        this.assetLines
      )
    },

    // TODO abstract to more genereal path finding algorithm and add to REC cache.
    // will require an inEdges and outEdges input. Perhpas as factory that return a specific path finder based on edges config.
    // Returns id value[][]
    getAssets: function (id) {
      const includes = this.$wob.queryEdges({
        source: id,
        label: 'https://w3id.org/rec#includes',
      })
      const locatedIn = this.$wob.queryEdges({
        target: id,
        label: 'https://w3id.org/rec#locatedIn',
      })
      const hasPart = this.$wob.queryEdges({
        source: id,
        label: 'https://w3id.org/rec#hasPart',
      })
      const isPartOf = this.$wob.queryEdges({
        target: id,
        label: 'https://w3id.org/rec#isPartOf',
      })
      console.log('get-assets', id, includes, locatedIn, hasPart, isPartOf)
      if (includes.length || locatedIn.length) {
        const children = includes.length
          ? includes.map((e) => this.getAssets(e.target.value))
          : locatedIn.map((e) => this.getAssets(e.source.value))
        return children
          .reduce((prev, curr) => prev.concat(curr), [])
          .map((p) => [id].concat(p))
      }
      if (hasPart.length || isPartOf.length) {
        // For assets that has subordinate/child assets both a path is generated for each asset.
        // TODO wont work if both direcitons ar ein use o same asset... refactor
        const children = hasPart.length
          ? hasPart.map((e) => this.getAssets(e.target.value))
          : isPartOf.map((e) => this.getAssets(e.source.value))
        return children
          .reduce((prev, curr) => prev.concat(curr), [])
          .map((p) => [id].concat(p))
          .concat([id])
      }
      // Leaf
      return [[id]]
    },

    getEvents: function (id) {
      const isPointOf = this.$wob.queryEdges({
        target: id,
        label: 'https://w3id.org/rec#isPointOf',
      })
      const lastKnownValue = this.$wob.queryEdges({
        source: id,
        label: 'https://w3id.org/rec#lastKnownValue',
      })
      console.log('get-points', id, isPointOf, lastKnownValue)
      if (isPointOf.length || lastKnownValue.length) {
        const children = isPointOf.length
          ? isPointOf.map((e) => this.getEvents(e.source.value))
          : lastKnownValue.map((e) => this.getEvents(e.target.value))
        return children
          .reduce((prev, curr) => prev.concat(curr), [])
          .map((p) => [id].concat(p))
      } else return [[id]]
    },
  },
}
</script>
